import type { LandingAction, LandingBlock, LandingPromoRuntime } from '@entities/LandingPage'

import LandingBannerBlock from '@features/landing-page-builder/ui/blocks/LandingBannerBlock'
import LandingButtonBlock from '@features/landing-page-builder/ui/blocks/LandingButtonBlock'
import LandingCategoryLinksBlock from '@features/landing-page-builder/ui/blocks/LandingCategoryLinksBlock'
import LandingFaqPreviewBlock from '@features/landing-page-builder/ui/blocks/LandingFaqPreviewBlock'
import LandingMenuBlock from '@features/landing-page-builder/ui/blocks/LandingMenuBlock'
import LandingPartnersBlock from '@features/landing-page-builder/ui/blocks/LandingPartnersBlock'
import LandingPrizeShowcaseBlock from '@features/landing-page-builder/ui/blocks/LandingPrizeShowcaseBlock'
import LandingProductsBlock from '@features/landing-page-builder/ui/blocks/LandingProductsBlock'
import LandingPromoCountersBlock from '@features/landing-page-builder/ui/blocks/LandingPromoCountersBlock'
import LandingStepsBlock from '@features/landing-page-builder/ui/blocks/LandingStepsBlock'
import LandingTextMaterialsBlock from '@features/landing-page-builder/ui/blocks/LandingTextMaterialsBlock'
import MainPageLikeBlock from '@features/landing-page-builder/ui/blocks/MainPageLikeBlock'

export interface LandingBlockProps {
  block: LandingBlock;
  payload: Record<string, unknown> | null;
  shopId: string;
  /** Shared page actions — blocks reference them by payload.actionId. */
  actions?: LandingAction[] | null;
  /** Promo runtime (promo landings only); source of truth for counters. */
  promo?: LandingPromoRuntime | null;
}

/*
  Block registry for landing pages.

  A landing is rendered like the main page: an ordered array of type/subtype/
  payload blocks. The types below are the union of
    - the main-page-like set, delegated to the existing main page renderers
      (MainPageLikeBlock) so a landing behaves like the main page for free, and
    - the landing extensions (event hero, faq, CTA, contest/event sections).

  Unknown types are skipped by the mapper, so the backend can ship a new block
  type without breaking existing pages.
*/
export const landingBlockComponents: Record<string, React.FC<LandingBlockProps>> = {
  // Landing extensions (and `banner`, which also handles the CMS form).
  banner: LandingBannerBlock,
  menu: LandingMenuBlock,
  products: LandingProductsBlock,
  textMaterials: LandingTextMaterialsBlock,
  category_links: LandingCategoryLinksBlock,
  faq_preview: LandingFaqPreviewBlock,
  button: LandingButtonBlock,
  promo_counters: LandingPromoCountersBlock,
  prize_showcase: LandingPrizeShowcaseBlock,
  steps_section: LandingStepsBlock,
  partners: LandingPartnersBlock,

  // Main-page-like blocks reused as-is.
  bannerGallery: MainPageLikeBlock,
  categories: MainPageLikeBlock,
  personalOffers: MainPageLikeBlock,
  coupons: MainPageLikeBlock,
  loyaltyLevel: MainPageLikeBlock,
  custom: MainPageLikeBlock,
}

export type LandingBlockType = keyof typeof landingBlockComponents
